import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEvents } from '../../context/EventsContext';
import { formatDate, formatPrice, reservationStatuses } from '../../utils/format';
import { mediaUrl } from '../../utils/format';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  ArrowLeft, Users, DollarSign, Clock, Mail,
  Phone, CreditCard, MessageSquare, ChevronDown,
  ChevronRight, CheckCircle2, XCircle, AlertCircle,
  Timer, Send, Armchair, LayoutGrid, ListChecks
} from 'lucide-react';

const statusIcon = {
  confirmed: <CheckCircle2 className="w-3.5 h-3.5" />,
  paid: <Timer className="w-3.5 h-3.5" />,
  rejected: <XCircle className="w-3.5 h-3.5" />,
  expired: <XCircle className="w-3.5 h-3.5" />,
  cancelled: <XCircle className="w-3.5 h-3.5" />,
  pending: <AlertCircle className="w-3.5 h-3.5" />
};

const formatDateTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

import { getChairPositionsWithOverrides } from '../../utils/chairPositions';

const AdminEventDetail = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { getEventDetail, getReservations, confirmReservation, rejectReservation } = useEvents();

  const [event, setEvent] = useState(null);
  const [layout, setLayout] = useState({ tables: [], chairs: [], features: [] });
  const [reservations, setReservations] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [selectedChairId, setSelectedChairId] = useState(null);
  const [expandedReservation, setExpandedReservation] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [eventData, resData] = await Promise.all([
          getEventDetail(eventId),
          getReservations(eventId)
        ]);
        setEvent(eventData);
        setLayout(eventData.layout || { tables: [], chairs: [], features: [] });
        setReservations(resData || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingData(false);
      }
    };
    load();
  }, [eventId, getEventDetail, getReservations]);

  const chairs = layout.chairs || [];
  const tables = layout.tables || [];

  const reservationsByChair = useMemo(() => {
    const map = {};
    reservations.forEach(r => {
      (r.chairIds || []).forEach(cid => {
        if (!map[cid]) map[cid] = [];
        map[cid].push(r);
      });
    });
    const priority = { paid: 0, confirmed: 1, pending: 2, rejected: 3, expired: 4, cancelled: 5 };
    Object.keys(map).forEach(cid => {
      map[cid].sort((a, b) => (priority[a.status] ?? 5) - (priority[b.status] ?? 5) || new Date(b.created_at) - new Date(a.created_at));
    });
    return map;
  }, [reservations]);

  const chairsByTable = useMemo(() => {
    const map = {};
    chairs.forEach(c => {
      if (!map[c.tableId]) map[c.tableId] = [];
      map[c.tableId].push(c);
    });
    return map;
  }, [chairs]);

  const selectedChair = chairs.find(c => c.id === selectedChairId);
  const selectedReservations = selectedChairId ? (reservationsByChair[selectedChairId] || []) : [];

  const highlightedChairIds = useMemo(() => {
    if (!selectedChairId) return new Set();
    const topRes = (reservationsByChair[selectedChairId] || [])[0];
    if (!topRes) return new Set();
    return new Set(topRes.chairIds || []);
  }, [selectedChairId, reservationsByChair]);

  const totalReservations = reservations.length;
  const activeReservations = reservations.filter(r => r.status === 'paid' || r.status === 'confirmed').length;
  const pendingConfirm = reservations.filter(r => r.status === 'paid').length;

  const getChairColor = (chair) => {
    const cRes = reservationsByChair[chair.id];
    if (!cRes || cRes.length === 0) return 'bg-emerald-900/60 border-emerald-600/60 text-emerald-300';
    const topStatus = cRes[0].status;
    if (topStatus === 'confirmed') return 'bg-emerald-700/70 border-emerald-400 text-white';
    if (topStatus === 'paid') return 'bg-amber-700/70 border-amber-400 text-white';
    return 'bg-zinc-700/50 border-zinc-500/50 text-zinc-400';
  };

  const handleConfirm = async (resId) => {
    await confirmReservation(resId, 'Confirmada por administrador');
    setReservations(prev => prev.map(r => r.id === resId ? { ...r, status: 'confirmed' } : r));
  };

  const handleReject = async (resId) => {
    await rejectReservation(resId, rejectReason.trim());
    setReservations(prev => prev.map(r => r.id === resId ? { ...r, status: 'rejected', note: rejectReason.trim() } : r));
    setRejectingId(null);
    setRejectReason('');
  };

  if (loadingData) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-muted-foreground">Cargando...</div>;
  if (!event) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-muted-foreground">Evento no encontrado</div>;

  return (
    <div data-testid="admin-event-detail" className="min-h-screen bg-[#050505]">
      <header className="border-b border-border bg-secondary/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Volver</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-serif font-semibold text-foreground truncate">{event.name}</h1>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{formatDate(event.date)} · {event.time} hrs</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Button data-testid="view-reservations-summary-btn" variant="outline" size="sm" onClick={() => navigate(`/admin/events/${eventId}/reservations`)} className="border-border text-muted-foreground hover:text-foreground">
              <ListChecks className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Resumen</span>
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/events/${eventId}/layout`)} className="border-border text-muted-foreground hover:text-foreground">
              <LayoutGrid className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Editar Layout</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-4 sm:mb-6">
          <div className="bg-secondary border border-border rounded-lg p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-500/10 flex items-center justify-center"><Users className="w-4 h-4 text-blue-400" /></div>
            <div><p className="text-xl font-bold text-foreground">{totalReservations}</p><p className="text-xs text-muted-foreground">Reservaciones</p></div>
          </div>
          <div className="bg-secondary border border-border rounded-lg p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-500/10 flex items-center justify-center"><CheckCircle2 className="w-4 h-4 text-emerald-400" /></div>
            <div><p className="text-xl font-bold text-foreground">{activeReservations}</p><p className="text-xs text-muted-foreground">Activas</p></div>
          </div>
          <div className="bg-secondary border border-border rounded-lg p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-500/10 flex items-center justify-center"><Timer className="w-4 h-4 text-amber-400" /></div>
            <div><p className="text-xl font-bold text-foreground">{pendingConfirm}</p><p className="text-xs text-muted-foreground">Por confirmar</p></div>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-4 sm:gap-6">
          <div className="lg:col-span-3">
            <div className="bg-secondary border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-foreground">Plano — Sillas</h3>
                <p className="text-xs text-muted-foreground">Selecciona una silla para ver sus reservas</p>
              </div>
              <div className="relative w-full bg-background/50 rounded-lg border border-border overflow-hidden" style={{ paddingBottom: '60%' }}>
                <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `linear-gradient(rgba(234,88,12,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(234,88,12,0.03) 1px,transparent 1px)`, backgroundSize: '5% 5%' }} />
                {(layout.features || []).map(f => (
                  <div key={f.id} className="absolute flex items-center justify-center rounded text-xs bg-muted/50 border border-border text-muted-foreground" style={{ left: `${f.x}%`, top: `${f.y}%`, width: `${f.width}%`, height: `${f.height}%` }}>{f.label || f.type}</div>
                ))}
                {tables.map(t => (
                  <div key={t.id} className={`absolute flex items-center justify-center border border-border/40 bg-muted/15 text-muted-foreground/40 ${t.shape === 'circle' ? 'rounded-full' : 'rounded-lg'}`} style={{ left: `${t.x}%`, top: `${t.y}%`, width: `${t.width}%`, height: `${t.height}%` }}>
                    <span className="text-[9px]">{t.label}</span>
                  </div>
                ))}
                {tables.map(t => {
                  const tChairs = chairsByTable[t.id] || [];
                  const positions = getChairPositionsWithOverrides(t, tChairs);
                  return tChairs.map((chair, idx) => {
                    const pos = positions[idx];
                    if (!pos) return null;
                    const isSelected = selectedChairId === chair.id;
                    const isHighlighted = !isSelected && highlightedChairIds.has(chair.id);
                    const hasPending = (reservationsByChair[chair.id] || []).some(r => r.status === 'paid');
                    return (
                      <button key={chair.id} data-testid={`admin-chair-${chair.id}`} onClick={() => setSelectedChairId(isSelected ? null : chair.id)}
                        className={`absolute w-[3%] h-[4%] rounded-full flex items-center justify-center transition-all
                          ${isSelected ? 'bg-primary border-2 border-primary shadow-[0_0_15px_rgba(234,88,12,0.5)] z-10 scale-125'
                            : isHighlighted ? 'bg-primary/60 border-2 border-primary/80 shadow-[0_0_10px_rgba(234,88,12,0.3)] z-[8] scale-110'
                            : `${getChairColor(chair)} border`} hover:brightness-125 cursor-pointer`}
                        style={{ left: `${pos.x}%`, top: `${pos.y}%` }}>
                        <span className="text-[7px] font-bold">{chair.label.slice(-1)}</span>
                        {hasPending && !isSelected && !isHighlighted && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-400 rounded-full animate-pulse" />}
                      </button>
                    );
                  });
                })}
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-900/60 border border-emerald-600/60" /> Sin reserva</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-emerald-700/70 border border-emerald-400" /> Confirmada</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-amber-700/70 border border-amber-400" /> Pagada</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-primary/60 border-2 border-primary/80" /> Misma reserva</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            {!selectedChairId ? (
              <div className="bg-secondary border border-border rounded-lg p-8 flex flex-col items-center justify-center text-center min-h-[300px]">
                <Armchair className="w-10 h-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">Selecciona una silla en el plano</p>
              </div>
            ) : (
              <div className="bg-secondary border border-border rounded-lg overflow-hidden">
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-foreground flex items-center gap-2"><Armchair className="w-4 h-4 text-primary" />Silla {selectedChair?.label}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">{tables.find(t => t.id === selectedChair?.tableId)?.label} · {formatPrice(selectedChair?.price, event.currency)}</p>
                    </div>
                    <Badge variant="outline" className="text-xs border-border">{selectedReservations.length} reserva{selectedReservations.length !== 1 ? 's' : ''}</Badge>
                  </div>
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  {selectedReservations.length === 0 ? (
                    <div className="p-6 text-center text-sm text-muted-foreground">Sin reservaciones</div>
                  ) : (
                    <div className="divide-y divide-border">
                      {selectedReservations.map((res) => {
                        const statusConf = reservationStatuses[res.status] || reservationStatuses.pending;
                        const isExpanded = expandedReservation === res.id;
                        const isActive = res.status === 'paid' || res.status === 'confirmed';
                        const resChairs = chairs.filter(c => (res.chairIds || []).includes(c.id));
                        const resTotal = resChairs.reduce((s, c) => s + (parseFloat(c.price) || 0), 0);
                        return (
                          <div key={res.id} data-testid={`reservation-${res.id}`} className={isActive ? 'bg-background/50' : ''}>
                            <button onClick={() => setExpandedReservation(isExpanded ? null : res.id)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/20 transition-colors">
                              <div className="shrink-0">{isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}</div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{res.full_name}</span>
                                  <span className="text-[10px] text-muted-foreground/60">{(res.chairIds || []).length} silla{(res.chairIds || []).length > 1 ? 's' : ''}</span>
                                </div>
                                <p className="text-xs text-muted-foreground/70 mt-0.5">{formatDateTime(res.created_at)}</p>
                              </div>
                              <Badge className={`text-[10px] shrink-0 ${statusConf.color}`}>{statusIcon[res.status]}<span className="ml-1">{statusConf.label}</span></Badge>
                            </button>
                            {isExpanded && (
                              <div className="px-4 pb-4 pl-10 space-y-2.5">
                                <div className="p-2 bg-muted/20 rounded text-xs space-y-1">
                                  {resChairs.map(c => {
                                    const t = tables.find(tb => tb.id === c.tableId);
                                    return <div key={c.id} className="flex justify-between text-muted-foreground"><span>{t?.label} · {c.label}</span><span>{formatPrice(c.price, event.currency)}</span></div>;
                                  })}
                                  <div className="border-t border-border pt-1 flex justify-between font-medium text-foreground"><span>Total</span><span className="text-primary">{formatPrice(resTotal, event.currency)}</span></div>
                                </div>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div className="flex items-center gap-1.5 text-muted-foreground"><Mail className="w-3 h-3 shrink-0" /><span className="truncate">{res.email}</span></div>
                                  <a href={`https://wa.me/${res.whatsapp.replace(/[\s()\-+]/g, '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-muted-foreground hover:text-emerald-400 transition-colors" onClick={e => e.stopPropagation()}>
                                    <Phone className="w-3 h-3 shrink-0" /><span className="underline underline-offset-2">{res.whatsapp}</span>
                                  </a>
                                  <div className="flex items-center gap-1.5 text-muted-foreground"><CreditCard className="w-3 h-3 shrink-0" /><span>{res.paymentMethod || '-'}</span></div>
                                  <div className="flex items-center gap-1.5 text-muted-foreground"><Clock className="w-3 h-3 shrink-0" /><span>{formatDateTime(res.created_at).split(' · ')[0]}</span></div>
                                </div>
                                {res.note && <div className="flex items-start gap-1.5 text-xs"><MessageSquare className="w-3 h-3 shrink-0 text-muted-foreground mt-0.5" /><span className="text-muted-foreground italic">{res.note}</span></div>}
                                {res.paymentProofUrl && (
                                  <div data-testid={`payment-proof-${res.id}`} className="pt-1">
                                    <p className="text-[10px] uppercase tracking-wide text-muted-foreground/60 mb-1.5">Comprobante de pago</p>
                                    <a
                                      href={mediaUrl(res.paymentProofUrl)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={e => e.stopPropagation()}
                                      className="block relative group/proof rounded-md overflow-hidden border border-border bg-background"
                                    >
                                      <img
                                        src={mediaUrl(res.paymentProofUrl)}
                                        alt="Comprobante"
                                        className="w-full max-h-48 object-contain bg-black/40"
                                      />
                                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/proof:opacity-100 transition-opacity flex items-center justify-center text-xs text-white">
                                        Click para ver completo
                                      </div>
                                    </a>
                                  </div>
                                )}
                                {res.status === 'paid' && rejectingId !== res.id && (
                                  <div className="flex gap-2 pt-1">
                                    <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={e => { e.stopPropagation(); handleConfirm(res.id); }}>
                                      <CheckCircle2 className="w-3 h-3 mr-1" />Confirmar
                                    </Button>
                                    <Button size="sm" variant="outline" className="h-7 text-xs border-border text-muted-foreground hover:text-foreground" onClick={e => { e.stopPropagation(); setRejectingId(res.id); setRejectReason(''); }}>
                                      <XCircle className="w-3 h-3 mr-1" />Rechazar
                                    </Button>
                                  </div>
                                )}
                                {res.status === 'paid' && rejectingId === res.id && (
                                  <div className="space-y-2 pt-1">
                                    <Textarea data-testid={`reject-reason-${res.id}`} placeholder="Motivo del rechazo..." value={rejectReason} onChange={e => setRejectReason(e.target.value)} className="bg-background border-border text-sm min-h-[60px] resize-none" onClick={e => e.stopPropagation()} />
                                    <div className="flex gap-2">
                                      <Button size="sm" className="h-7 text-xs bg-red-600 hover:bg-red-700" disabled={!rejectReason.trim()} onClick={e => { e.stopPropagation(); handleReject(res.id); }}>
                                        <Send className="w-3 h-3 mr-1" />Guardar Rechazo
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={e => { e.stopPropagation(); setRejectingId(null); }}>Cancelar</Button>
                                    </div>
                                  </div>
                                )}
                                {res.status === 'rejected' && res.note && (
                                  <div className="flex items-start gap-1.5 text-xs pt-1"><XCircle className="w-3 h-3 shrink-0 text-red-400 mt-0.5" /><span className="text-red-400/80 italic">Rechazada: {res.note}</span></div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminEventDetail;
